import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, FileText, Receipt, HardHat, Wrench, FolderArchive, Bot,
  Building2, ClipboardList, ShoppingCart, ShieldCheck, History, Settings, LogOut,
  CheckSquare, MessageSquare, Menu, X, Calendar as CalendarIcon, Target,
  Sparkles, Package, Wallet, Zap, FolderOpen, MessageCircle, MapPin,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import NotificationBell from '@/components/NotificationBell';

/**
 * Mobile header (top bar) — Reno Resolve.
 * - Barre du haut : logo + notifications + rôle + bouton menu hamburger.
 * - Strip horizontal de raccourcis (6 modules clés) pour l'accès 1-tap.
 * - Menu hamburger : drawer vertical avec TOUS les modules disponibles,
 *   groupé par section, respectant les permissions.
 */

// Raccourcis 1-tap (toujours visibles en haut, dans l'ordre d'usage).
const QUICK_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Accueil', module: 'dashboard', exact: true },
  { to: '/agenda', icon: CalendarIcon, label: 'Agenda', module: 'agenda' },
  { to: '/chantiers', icon: HardHat, label: 'Chantiers', module: 'chantiers' },
  { to: '/devis', icon: FileText, label: 'Devis', module: 'devis' },
  { to: '/factures', icon: Receipt, label: 'Factures', module: 'factures' },
  { to: '/taches', icon: CheckSquare, label: 'Tâches', module: 'taches' },
];

// Toutes les entrées, groupées par section pour le drawer.
const DRAWER_SECTIONS = [
  {
    title: 'Pilotage',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Tableau de bord', module: 'dashboard', exact: true },
      { to: '/agenda', icon: CalendarIcon, label: 'Agenda société', module: 'agenda' },
      { to: '/taches', icon: CheckSquare, label: 'Tâches', module: 'taches' },
      { to: '/discussions', icon: MessageSquare, label: 'Discussions', module: 'chat' },
    ],
  },
  {
    title: 'Clients & prospection',
    items: [
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'contacts' },
      { to: '/gestion-clients?category=collectivites', icon: Building2, label: 'Collectivités', module: 'gestion_clients' },
      { to: '/prospections', icon: Target, label: 'Prospections', module: 'prospections' },
    ],
  },
  {
    title: 'Devis, factures, achats',
    items: [
      { to: '/devis', icon: FileText, label: 'Devis', module: 'devis' },
      { to: '/factures', icon: Receipt, label: 'Factures (ventes)', module: 'factures' },
      { to: '/factures-achat', icon: ShoppingCart, label: "Factures d'achat", module: 'factures_achat' },
    ],
  },
  {
    title: 'Terrain',
    items: [
      { to: '/fiches-visite', icon: ClipboardList, label: 'Fiches Visite', module: 'fiches_visite' },
      { to: '/chantiers', icon: HardHat, label: 'Chantiers', module: 'chantiers' },
      { to: '/sous-traitants', icon: Wrench, label: 'Sous-traitants', module: 'sous_traitants' },
      { to: '/fournisseurs', icon: Package, label: 'Fournisseurs', module: 'sous_traitants' },
    ],
  },
  {
    title: 'IA & marketing',
    items: [
      { to: '/marketing', icon: Sparkles, label: 'Community Manager IA', module: 'marketing' },
      { to: '/agents', icon: Bot, label: 'Orchestrateur IA', module: 'agents_ia' },
    ],
  },
  {
    title: 'CEE & AIDE',
    items: [
      { to: '/cee/simulations', icon: Zap, label: 'Simulations', module: 'cee' },
      { to: '/cee/dossiers', icon: FolderOpen, label: 'Dossiers', module: 'cee' },
      { to: '/cee/zones', icon: MapPin, label: 'Vue par zone', module: 'cee' },
    ],
  },
  {
    title: 'Administratif',
    items: [
      { to: '/administratif', icon: FolderArchive, label: 'Administratif', module: 'administratif' },
    ],
  },
];

const ADMIN_ITEMS = [
  { to: '/comptabilite', icon: Wallet, label: 'Comptabilité' },
  { to: '/admin/users', icon: ShieldCheck, label: 'Utilisateurs' },
  { to: '/whatsapp/admin', icon: MessageCircle, label: 'WhatsApp' },
  { to: '/admin/activity-log', icon: History, label: 'Journal d\'activité' },
  { to: '/parametres', icon: Settings, label: 'Paramètres' },
];

function canSee(user, module) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const perms = user.permissions || {};
  const entry = perms[module];
  if (entry === undefined) return true;         // module manquant → default lecture (backfill dynamique côté backend)
  return !!entry?.read;
}

export default function MobileHeader() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isAdmin = user && user.role === 'admin';

  // Ferme le drawer sur changement de route
  useEffect(() => { setDrawerOpen(false); }, [location.pathname, location.search]);

  // Ferme sur touche Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const visibleQuick = QUICK_ITEMS.filter((it) => canSee(user, it.module));

  return (
    <div className="md:hidden bg-white border-b border-slate-200 sticky top-0 z-50" data-testid="mobile-header">
      {/* Top bar : logo + hamburger + rôle + bell + logout */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 gap-2">
        <button
          onClick={() => setDrawerOpen(true)}
          className="p-2 -ml-1 rounded-md text-slate-700 hover:bg-slate-100 active:bg-slate-200"
          data-testid="mobile-menu-btn"
          aria-label="Ouvrir le menu"
        >
          <Menu className="w-6 h-6" />
        </button>
        <img src="/logo.png" alt="Reno Resolve" className="h-7" />
        {user && (
          <div className="flex items-center gap-1">
            <NotificationBell />
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold hidden xs:inline-block">
              {user.role || 'user'}
            </span>
            <button
              onClick={logout}
              className="p-1.5 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"
              data-testid="mobile-logout-btn"
              aria-label="Se déconnecter"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Strip horizontal — 6 raccourcis 1-tap */}
      <nav className="flex items-stretch h-14 px-1 overflow-x-auto scrollbar-hide" data-testid="mobile-nav">
        {visibleQuick.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.exact}
            className={({ isActive }) =>
              `flex-1 min-w-[60px] flex flex-col items-center justify-center px-1 transition-colors border-b-2 ${
                isActive
                  ? 'text-blue-600 border-blue-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`
            }
            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-[10px] mt-0.5 font-medium whitespace-nowrap">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Drawer — tous les modules */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <button
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 bg-slate-900/50 z-[60] animate-in fade-in duration-150"
            data-testid="mobile-drawer-backdrop"
            aria-label="Fermer le menu"
          />
          {/* Panel */}
          <aside
            className="fixed inset-y-0 left-0 w-[85%] max-w-sm bg-white z-[70] shadow-2xl flex flex-col animate-in slide-in-from-left duration-200"
            data-testid="mobile-drawer"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <img src="/logo.png" alt="Reno Resolve" className="h-8" />
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-md text-slate-600 hover:bg-slate-100"
                data-testid="mobile-drawer-close-btn"
                aria-label="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer user card */}
            {user && (
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 text-white flex items-center justify-center font-bold text-sm shrink-0">
                    {(user.name || user.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{user.name || 'Utilisateur'}</div>
                    <div className="text-xs text-slate-500 truncate">{user.email}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold shrink-0">
                    {user.role || 'user'}
                  </span>
                </div>
              </div>
            )}

            {/* Drawer scrollable content */}
            <div className="flex-1 overflow-y-auto py-2">
              {DRAWER_SECTIONS.map((section) => {
                const visible = section.items.filter((it) => canSee(user, it.module));
                if (visible.length === 0) return null;
                return (
                  <div key={section.title} className="mb-3">
                    <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">
                      {section.title}
                    </div>
                    {visible.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.exact}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2.5 transition-colors ${
                            isActive
                              ? 'bg-blue-50 text-blue-700 border-l-[3px] border-blue-600'
                              : 'text-slate-700 hover:bg-slate-50 border-l-[3px] border-transparent'
                          }`
                        }
                        data-testid={`drawer-item-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <item.icon className="w-5 h-5 shrink-0" />
                        <span className="text-sm font-medium">{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                );
              })}

              {isAdmin && (
                <div className="mb-3 border-t border-slate-200 pt-2">
                  <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider font-bold text-red-500">
                    Administration
                  </div>
                  {ADMIN_ITEMS.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 transition-colors ${
                          isActive
                            ? 'bg-red-50 text-red-700 border-l-[3px] border-red-600'
                            : 'text-slate-700 hover:bg-slate-50 border-l-[3px] border-transparent'
                        }`
                      }
                      data-testid={`drawer-admin-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <item.icon className="w-5 h-5 shrink-0" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            {/* Drawer footer — logout */}
            {user && (
              <div className="p-3 border-t border-slate-200">
                <button
                  onClick={logout}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-100 hover:bg-red-50 hover:text-red-700 text-slate-700 text-sm font-medium transition-colors"
                  data-testid="drawer-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Se déconnecter
                </button>
              </div>
            )}
          </aside>
        </>
      )}
    </div>
  );
}
